import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkAuthAndRedirect } from "../api/auth";
import "./Dashboard.css";
import Header from "../components/Header";
//import MaintenancePage from "./MaintenancePage";

// Material UIコンポーネント
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery
} from "@mui/material";

// アイコン
import MapIcon from '@mui/icons-material/Map';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StorageIcon from '@mui/icons-material/Storage';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // const [isMaintenanceMode, setIsMaintenanceMode] = useState(false); // メンテナンスモード状態を削除
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 管理者メールアドレス一覧（将来の機能拡張用に保持）
  const ADMIN_EMAILS = [
    "new_user@example.com",
    "hirotoisogawa@gmail.com"
  ];
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await checkAuthAndRedirect(navigate);
        if (userData) {
          setUser(userData);
          // メンテナンス判定を削除：全ユーザーがアクセス可能
          // if (!ADMIN_EMAILS.includes(userData.email)) {
          //   setIsMaintenanceMode(true);
          // }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
      
    fetchUser();
  }, [navigate]);

  // ローディング画面
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // 主要機能セクション
    // メンテナンス画面表示を削除
  // if (isMaintenanceMode) {
  //   return <MaintenancePage />;
  // }

  // 既存のダッシュボード機能（変更なし）
  const mainFeaturesItems = [
    {
      title: "データ登録",
      description: "顧客・製品・CSVデータの登録",
      icon: <AddCircleOutlineIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      onClick: () => navigate("/data-registration"),
      color: "rgba(255, 152, 0, 0.1)"
    },
    {
      title: "データ分析",
      description: "供給データの詳細分析と統計",
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
              onClick: () => navigate("/analysis/product-dashboard"), // 直接ProductAnalysisDashboardに遷移
      color: "rgba(255, 152, 0, 0.1)",
      highlight: true, // NEWバッジ表示フラグ
      isNew: true // 新機能フラグ
    }
  ];

  // 管理機能セクション
  const managementItems = [
    {
      title: "設定",
      description: "アカウント・通知・システム設定",
      icon: <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      onClick: () => navigate("/setting-Home"),
      color: "rgba(255, 152, 0, 0.1)"
    }
  ];

  // 近日公開予定の機能
  const futureFeatures = [
    {
      title: "補充予測",
      description: "AIによる補充タイミングの予測",
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
      disabled: true,
      color: "rgba(0, 0, 0, 0.04)"
    },
    {
      title: "スケジュール",
      description: "訪問予定の管理",
      icon: <EventNoteIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
      disabled: true,
      color: "rgba(0, 0, 0, 0.04)"
    },
    {
      title: "訪問提案",
      description: "最適な顧客訪問ルートの提案",
      icon: <MapIcon sx={{ fontSize: 40, color: 'text.secondary' }} />,
      disabled: true,
      color: "rgba(0, 0, 0, 0.04)"
    }
  ];

  return (
    <Box sx={{ bgcolor: '#F5F7FA', minHeight: '100vh' }}>
      <Header username={user?.username} />
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 4,
          // PWAボトムナビゲーション対応：安全マージンを確保
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' },
          px: { xs: 2, sm: 3 }
        }}
      >
        {/* ようこそメッセージ */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            background: 'linear-gradient(to right, #ff9800, #ff7b00)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DashboardIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                ようこそ、{user?.username || 'ユーザー'}さん
              </Typography>
              <Typography variant="subtitle1">
                作業を選択してください
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 主要機能セクション */}
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ mb: 2, fontWeight: 500, pl: 1 }}
        >
          主要機能
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {mainFeaturesItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={item.highlight ? 4 : 2}
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  border: item.highlight ? '2px solid' : 'none',
                  borderColor: item.highlight ? 'primary.main' : 'transparent',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: item.highlight ? 8 : 6
                  }
                }}
              >
                {/* NEWバッジ - isNewフラグで制御 */}
                {item.isNew && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'error.main',
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      zIndex: 2,
                      boxShadow: 2
                    }}
                  >
                    NEW
                  </Box>
                )}
                <CardActionArea 
                  onClick={item.onClick}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    minHeight: { xs: 140, sm: 120 } // タッチしやすいサイズ
                  }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: item.color,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      mb: 2
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 500, textAlign: 'center' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                    {item.description}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 管理機能セクション */}
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ mb: 2, fontWeight: 500, pl: 1, mt: 4 }}
        >
          管理機能
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {managementItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardActionArea 
                  onClick={item.onClick}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    minHeight: { xs: 140, sm: 120 } // タッチしやすいサイズ
                  }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: item.color,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      mb: 2
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 500, textAlign: 'center' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                    {item.description}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 近日公開予定の機能 */}
        <Box sx={{ mt: 5, mb: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography 
            variant="h6" 
            component="h2" 
            sx={{ mb: 2, color: 'text.secondary', fontWeight: 500, pl: 1 }}
          >
            近日公開予定の機能
          </Typography>
          
          <Grid container spacing={2}>
            {futureFeatures.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={1}
                  sx={{ 
                    height: '100%',
                    opacity: item.disabled ? 0.7 : 1,
                    bgcolor: item.color
                  }}
                >
                  <CardContent sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    textAlign: 'center',
                    minHeight: { xs: 140, sm: 120 } // 統一されたサイズ
                  }}>
                    {item.icon}
                    <Typography variant="h6" component="h3" sx={{ mt: 2, fontWeight: 500, color: 'text.secondary' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {item.description}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 2, color: 'primary.main' }}>
                      近日公開予定
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;